import { z } from "zod";

import { type CarePlanStep, StepType, isStepEmpty } from "./types";

const carePlanBaseSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  tagIds: z.array(z.string()).min(1, "At least one tag is required"),
});

function hasEmptySteps(steps: CarePlanStep[]): boolean {
  return steps.some((step) => {
    if (isStepEmpty(step)) return true;
    if (step.type === StepType.DECISION) {
      return (step.branches ?? []).some((branch) =>
        hasEmptySteps(branch.steps),
      );
    }
    return false;
  });
}

function hasInvalidDecisions(steps: CarePlanStep[]): boolean {
  return steps.some((step) => {
    if (step.type === StepType.DECISION) {
      const branches = step.branches ?? [];
      if (branches.length < 2) return true;
      if (branches.some((branch) => branch.steps.length === 0)) return true;
      return branches.some((branch) => hasInvalidDecisions(branch.steps));
    }
    return false;
  });
}

const templateStepsSchema = z
  .array(z.custom<CarePlanStep>())
  .min(1, "At least one step is required")
  .refine((steps) => !hasInvalidDecisions(steps), {
    message:
      "Each decision must have at least two branches, and each branch must have at least one step.",
  });

const patientStepsSchema = templateStepsSchema.refine(
  (steps) => !hasEmptySteps(steps),
  {
    message:
      "All steps must have content. Add a service, medication, measurement, task title, or description to each step.",
  },
);

export const carePlanTemplateSchema = carePlanBaseSchema.extend({
  steps: templateStepsSchema.optional(),
});

export const patientCarePlanSchema = carePlanBaseSchema.extend({
  steps: patientStepsSchema,
});
